import React, { useState } from 'react';
import axios from 'axios';

const SendMessage = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [message, setMessage] = useState('');
  const [pdfUrl, setPdfUrl] = useState('');
  const [fileName, setFileName] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [messageType, setMessageType] = useState('text');

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

  const clearMessages = () => {
    setSuccess('');
    setError('');
  };

  const sendTextMessage = async (e) => {
    e.preventDefault();
    
    if (!phoneNumber || !message) {
      setError('Phone number and message are required');
      return;
    }

    setLoading(true);
    clearMessages();

    try {
      const response = await axios.post(`${API_BASE_URL}/send-message`, {
        to: phoneNumber,
        message: message
      });

      setSuccess('Message sent successfully!');
      setMessage('');
    } catch (err) {
      setError(err.response?.data || 'Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const sendPdfMessage = async (e) => {
    e.preventDefault();
    
    if (!phoneNumber || !pdfUrl) {
      setError('Phone number and PDF URL are required');
      return;
    }

    setLoading(true);
    clearMessages();

    try {
      const response = await axios.post(`${API_BASE_URL}/send-message-pdf`, {
        to: phoneNumber,
        pdfUrl: pdfUrl,
        fileName: fileName || 'document.pdf'
      });

      setSuccess('PDF sent successfully!');
      setPdfUrl('');
      setFileName('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send PDF');
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    // Add country code if not present
    if (digits.length > 0 && !digits.startsWith('62')) {
      if (digits.startsWith('0')) {
        return '62' + digits.substring(1);
      } else if (digits.startsWith('8')) {
        return '62' + digits;
      }
    }
    
    return digits;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhoneNumber(formatted);
  };

  return (
    <div>
      <div className="card">
        <h2 className="card-title">Send WhatsApp Message</h2>
        
        {/* Message Type Selector */}
        <div className="form-group">
          <label className="form-label">Message Type:</label>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="radio"
                value="text"
                checked={messageType === 'text'}
                onChange={(e) => setMessageType(e.target.value)}
              />
              Text Message
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="radio"
                value="pdf"
                checked={messageType === 'pdf'}
                onChange={(e) => setMessageType(e.target.value)}
              />
              PDF Document
            </label>
          </div>
        </div>

        {/* Phone Number Input */}
        <div className="form-group">
          <label className="form-label">Phone Number:</label>
          <input
            type="text"
            className="form-input"
            value={phoneNumber}
            onChange={handlePhoneChange}
            placeholder="e.g., 6287780300386"
            disabled={loading}
          />
          <small style={{ color: '#666', fontSize: '0.8rem' }}>
            Enter phone number with country code (62 for Indonesia)
          </small>
        </div>

        {/* Success/Error Messages */}
        {success && <div className="success">{success}</div>}
        {error && <div className="error">{error}</div>}

        {/* Text Message Form */}
        {messageType === 'text' && (
          <form onSubmit={sendTextMessage}>
            <div className="form-group">
              <label className="form-label">Message:</label>
              <textarea
                className="form-input form-textarea"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Enter your message here..."
                disabled={loading}
                rows={5}
              />
            </div>

            <button 
              type="submit" 
              className="btn" 
              disabled={loading || !phoneNumber || !message}
            >
              {loading ? 'Sending...' : 'Send Text Message'}
            </button>
          </form>
        )}

        {/* PDF Message Form */}
        {messageType === 'pdf' && (
          <form onSubmit={sendPdfMessage}>
            <div className="form-group">
              <label className="form-label">PDF URL:</label>
              <input
                type="url"
                className="form-input"
                value={pdfUrl}
                onChange={(e) => setPdfUrl(e.target.value)}
                placeholder="https://example.com/document.pdf"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label className="form-label">File Name (optional):</label>
              <input
                type="text"
                className="form-input"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                placeholder="document.pdf"
                disabled={loading}
              />
            </div>

            <button 
              type="submit" 
              className="btn" 
              disabled={loading || !phoneNumber || !pdfUrl}
            >
              {loading ? 'Sending...' : 'Send PDF Document'}
            </button>
          </form>
        )}

        {/* Quick Actions */}
        <div style={{ marginTop: '2rem', padding: '1rem', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
          <h4 style={{ marginBottom: '1rem', color: '#333' }}>Quick Actions:</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            <button 
              className="btn" 
              onClick={() => setMessage('Hello! This is a test message from WhatsApp Bot.')}
              disabled={loading || messageType !== 'text'}
              style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}
            >
              Test Message
            </button>
            <button 
              className="btn" 
              onClick={() => setMessage('Terima kasih telah menghubungi kami. Ada yang bisa kami bantu?')}
              disabled={loading || messageType !== 'text'}
              style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}
            >
              Welcome Message
            </button>
            <button 
              className="btn" 
              onClick={() => {
                setPhoneNumber('');
                setMessage('');
                setPdfUrl('');
                setFileName('');
                clearMessages();
              }}
              disabled={loading}
              style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}
            >
              Clear All
            </button>
          </div>
        </div>

        {/* Tips */}
        <div style={{ marginTop: '2rem', padding: '1rem', backgroundColor: '#e8f5e8', borderRadius: '8px' }}>
          <h4 style={{ marginBottom: '0.5rem', color: '#2e7d32' }}>💡 Tips:</h4>
          <ul style={{ margin: 0, paddingLeft: '1.5rem', color: '#2e7d32' }}>
            <li>Make sure the WhatsApp connection is active (check QR Code page)</li>
            <li>Phone numbers should include country code (e.g., 62 for Indonesia)</li>
            <li>PDF URLs must be publicly accessible</li>
            <li>All sent messages will be logged and visible in Message Logs</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SendMessage;